// CORE
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// MUI
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import MenuButton from '@material-ui/icons/MoreVert';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

// DATA
import { useDispatch } from 'react-redux';

// CUSTOM COMPONENTS
import useQData from 'components/QData/useQData';
import { showDeleteReminderDialog } from 'components/Dialogs/DeleteReminderDialog/actions';
import { showMatchTxnDialog } from 'components/Dialogs/MatchTxnDialog/actions';
import { showViewSeriesDialog } from 'components/Dialogs/ViewSeriesDialog/actions';

import EditReminderDialog from './editReminderDialog';


const useStyles = makeStyles((theme) => ({
  grey: {
    color: theme.palette.greyScale[2],
  },
  light: {
    height: 32,
    width: 32,
    padding: 4,
    opacity: .5,
    '&:hover': {
      opacity: .9,
      color: theme.palette.greyScale[2],
    },
  },
  menu: {
    marginLeft: 8,
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.palette.greyScale[5],
    },
  },
  square: {
    height: 24,
    width: 24,
    color: theme.palette.greyScale[2],
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.palette.greyScale[5],
    },
  },
  small: {
    height: 24,
    weight: 24,
  },
  greyRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 52,
    backgroundColor: theme.palette.greyScale[6],
  },
  noPadding: {
    padding: 0,
    minWidth: 180,
  },
}));



// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                   Menu Component                    - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function UpcomingMenu(props) {
  const dispatch = useDispatch();
  const { txn, square, showView, closeOnClick, style, deleteReminder, linkTxn, editTxn, isSpendingPlan } = props;
  const { qDataDeleteTransactions } = useQData();
  const classes = useStyles();

  const [menuAnchor, setMenuAnchor] = useState(false);
  const [edit, setEdit] = useState(false);

  const buttonClick = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.target);
  };

  // Menu Actions
  // # --------------------------------------------
  const deleteInstance = () => {
    dispatch(showDeleteReminderDialog(txn));
    closeOnClick && setMenuAnchor(false);
  };
  const startMatch = () => {
    dispatch(showMatchTxnDialog(txn));
    closeOnClick && setMenuAnchor(false);
  };

  // edit
  const openEdit = () => {
    setEdit(true);
    closeOnClick && setMenuAnchor(false);
  };
  const closeEditDialog = () => setEdit(false);
  // view
  const openView = () => {
    dispatch(showViewSeriesDialog(txn));
    closeOnClick && setMenuAnchor(false);
  };

  const onDelete = () => {
    qDataDeleteTransactions([txn]);
    setMenuAnchor(false);
  };

  // render return
  return (
    <div role="presentation" onClick={(e) => e.stopPropagation()} style={style}>
      <ButtonBase
        className={classNames(classes.menu, square && classes.square)}
        onClick={buttonClick}
        focusRipple
      >
        <MenuButton className={classNames(classes.light, square && classes.small)} />
      </ButtonBase>

      {menuAnchor &&
      <Menu
        id="account-settings-menu"
        anchorEl={menuAnchor}
        keepMounted
        open
        onClose={() => setMenuAnchor(false)}
        MenuListProps={{
          classes: {
            root: classes.noPadding,
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {linkTxn &&
        <MenuItem
          id="menu-link"
          onClick={startMatch}
          button
          divider
        >
          Link to transaction
        </MenuItem>}

        {editTxn &&
        <MenuItem
          id="menu-edit"
          onClick={openEdit}
          button
          divider
        >
          {txn.source === 'SCHEDULED_TRANSACTION_PENDING' ? 'Edit reminder' : 'Edit Transaction'}
        </MenuItem>}

        <MenuItem
          id="menu-delete"
          onClick={deleteReminder ? deleteInstance : onDelete}
          button
          divider
        >
          {deleteReminder ? 'Delete reminder' : 'Delete Transaction'}
        </MenuItem>

        {showView &&
        <MenuItem
          id="menu-view"
          onClick={openView}
          button
          classes={{
            root: classes.greyRow,
          }}
        >
          View series
        </MenuItem>
        }
      </Menu>
      }

      {edit &&
      <EditReminderDialog txn={txn} onClose={closeEditDialog} linkTxn={startMatch} context={isSpendingPlan ? 'spending-plan' : ''}/>
      }
    </div>
  );
}

UpcomingMenu.defaultProps = {
  txn: {},
  square: false,
  showView: true,
  closeOnClick: true,
  style: {},
  deleteReminder: true,
  linkTxn: true,
  editTxn: true,
};

UpcomingMenu.propTypes = {
  txn: PropTypes.object,
  square: PropTypes.bool, // make the button square
  showView: PropTypes.bool, // show the 'view series' option
  closeOnClick: PropTypes.bool, // close the menu on clicking a menuItem
  style: PropTypes.object,
  deleteReminder: PropTypes.bool, // show delete reminder option
  linkTxn: PropTypes.bool, // show link reminder option
  editTxn: PropTypes.bool, // show edit reminder option
  isSpendingPlan: PropTypes.bool,
};

export default UpcomingMenu
